import { Injectable } from '@angular/core';
import { IUnsavedChangesComponent } from '../interfaces/unsaved-changes-component.interface';

/**
 * Generic service to handle unsaved changes confirmation modal
 * Works with any component that implements IUnsavedChangesComponent interface
 * 
 * Usage:
 * 1. Component implements IUnsavedChangesComponent
 * 2. Component registers itself: service.registerComponent(this)
 * 3. Use navigateWithCheck() for navigation actions
 * 4. Guard uses canDeactivateGuard() for route protection
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesGuardService {
  private componentRef: IUnsavedChangesComponent | null = null;
  private modalVisible: boolean = false;

  /**
   * Register a component that needs unsaved changes protection
   * Component must implement IUnsavedChangesComponent interface
   * Called from component.ngOnInit()
   */
  registerComponent(component: IUnsavedChangesComponent): void {
    this.componentRef = component;
  }

  /**
   * Unregister component
   * Called from component.ngOnDestroy()
   */
  unregisterComponent(): void {
    this.componentRef = null;
  }

  /**
   * Check if component has unsaved changes
   * Delegates to component's hasUnsavedChanges() method
   */
  hasUnsavedChanges(): boolean {
    if (!this.componentRef) {
      return false;
    }
    
    // Component decides how to check for unsaved changes
    return this.componentRef.hasUnsavedChanges();
  }

  /**
   * Main navigation wrapper method
   * 
   * @param actionType - String identifier for the action (e.g., 'nextDocument', 'navigateToDocument')
   * @param action - Callback function to execute if navigation is allowed
   * @returns Promise<boolean> - true if navigation proceeded, false if cancelled
   */
  async navigateWithCheck(
    actionType: string,
    action: () => void | Promise<void>
  ): Promise<boolean> {
    
    // Log the action type for debugging
    console.log(`[UnsavedChangesGuard] Action type: ${actionType}`);
    
    // Check if there are unsaved changes
    if (!this.hasUnsavedChanges()) {
      // No unsaved changes, proceed immediately
      await action();
      return true;
    }

    // Has unsaved changes - show modal
    const choice = await this.showConfirmationModal(actionType);
    
    switch (choice) {
      case 'save':
        // Save changes first, then proceed
        await this.saveChanges();
        await action();
        return true;
        
      case 'discard':
        // Discard changes, then proceed
        this.discardChanges();
        await action();
        return true;
        
      case 'cancel':
        // User cancelled - don't proceed
        return false;
        
      default:
        return false;
    }
  }

  /**
   * Show confirmation modal
   * Returns user's choice: 'save', 'discard', or 'cancel'
   * 
   * This method is used by:
   * - navigateWithCheck() for button actions
   * - canDeactivateGuard() for route changes
   * - Browser event handlers (popstate, beforeunload)
   */
  async showConfirmationModal(actionType: string): Promise<'save' | 'discard' | 'cancel'> {
    if (!this.componentRef) {
      return 'cancel';
    }

    // Set modal visible flag
    this.modalVisible = true;
    this.componentRef.showUnsavedChangesModal = true;
    this.componentRef.unsavedChangesActionType = actionType;

    // Return a promise that resolves when user makes a choice
    return new Promise((resolve) => {
      // Store resolve function in component so modal can call it
      if (this.componentRef) {
        this.componentRef.unsavedChangesResolve = resolve;
      } else {
        resolve('cancel');
      }
    });
  }

  /**
   * Method specifically for route guard (canDeactivate)
   * Returns Promise<boolean> - true to allow navigation, false to cancel
   */
  async canDeactivateGuard(): Promise<boolean> {
    if (!this.hasUnsavedChanges()) {
      return true; // No unsaved changes, allow navigation
    }

    const choice = await this.showConfirmationModal('routeChange');
    
    switch (choice) {
      case 'save':
        await this.saveChanges();
        return true; // Allow navigation after save
        
      case 'discard':
        this.discardChanges();
        return true; // Allow navigation after discard
        
      case 'cancel':
        return false; // Cancel navigation, stay on page
        
      default:
        return false;
    }
  }

  /**
   * Handle user choice from modal
   * Called by component when user clicks a button in the modal
   */
  handleUserChoice(choice: 'save' | 'discard' | 'cancel'): void {
    this.modalVisible = false;
    if (this.componentRef) {
      this.componentRef.showUnsavedChangesModal = false;
      if (this.componentRef.unsavedChangesResolve) {
        this.componentRef.unsavedChangesResolve(choice);
        this.componentRef.unsavedChangesResolve = null;
      }
    }
  }

  /**
   * Save changes
   * Delegates to component's saveChanges() method
   */
  private async saveChanges(): Promise<void> {
    if (!this.componentRef) {
      return;
    }

    // Component decides how to save changes
    if (this.componentRef.isSavingUnsavedChanges !== undefined) {
      this.componentRef.isSavingUnsavedChanges = true;
    }
    
    await this.componentRef.saveChanges();
    
    if (this.componentRef.isSavingUnsavedChanges !== undefined) {
      this.componentRef.isSavingUnsavedChanges = false;
    }
  }

  /**
   * Discard changes
   * Delegates to component's discardChanges() method
   */
  private discardChanges(): void {
    if (!this.componentRef) {
      return;
    }

    // Component decides how to discard changes
    this.componentRef.discardChanges();
  }
}
